// Language picker (English / Español). Language names are shown in their
// own language so a player can always find theirs.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';
import { LOCALES, LOCALE_FLAGS, LOCALE_NAMES, isLocale, useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { useSettings } from '../../settings/useSettings.js';

export function LocaleSelect() {
  const t = useT();
  const locale = useSettings((s) => s.locale);
  const setLocale = useSettings((s) => s.setLocale);
  const label = `${t('locale.label')}: ${LOCALE_NAMES[locale]}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="locale-select"
          {...appDom({
            id: 'locale-select',
            type: 'Select',
            role: 'select',
            scope: 'header',
            label: 'Language',
          })}
          data-locale={locale}
          aria-label={label}
          title={label}
          className="h-8 gap-1 px-2"
        >
          <Languages className="size-4" />
          <span
            className="hidden text-sm leading-none sm:inline"
            aria-hidden
            data-testid={`locale-flag-${locale}`}
          >
            {LOCALE_FLAGS[locale]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[9rem]"
        data-testid="locale-menu"
        {...appDom({ id: 'locale-menu', type: 'Dropdown', scope: 'menu' })}
      >
        <DropdownMenuLabel>{t('locale.label')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(v) => {
            if (isLocale(v)) setLocale(v);
          }}
        >
          {LOCALES.map((code) => (
            <DropdownMenuRadioItem
              key={code}
              value={code}
              lang={code}
              data-testid={`locale-${code}`}
              {...appDom({ id: `locale-${code}`, role: 'button', scope: 'menu' })}
            >
              <span aria-hidden className="mr-1">
                {LOCALE_FLAGS[code]}
              </span>
              {LOCALE_NAMES[code]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
